import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ApiError } from '../lib/api';

const LEN = 6;

export function Otp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp } = useApp();
  const state = (location.state || {}) as { email?: string; demoOtp?: string };
  const email = state.email;

  const [digits, setDigits] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  async function verify(code: string) {
    if (verifying) return;
    setVerifying(true);
    setError('');
    try {
      await verifyOtp(String(email), code);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Verification failed. Please try again.');
      setVerifying(false);
      setDigits('');
    }
  }

  function onChange(v: string) {
    const clean = v.replace(/[^0-9]/g, '').slice(0, LEN);
    setDigits(clean);
    setError('');
    if (clean.length === LEN) verify(clean);
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 420, textAlign: 'center', padding: 28 }}>
        <div className="center" style={{ width: 72, height: 72, borderRadius: 20, background: 'var(--primary-soft)', margin: '0 auto', fontSize: 30 }}>💬</div>
        <h2 style={{ fontSize: 22, fontWeight: 900, marginTop: 16 }}>Verify your number</h2>
        <p className="muted" style={{ marginTop: 8 }}>
          We&apos;ve sent a 6-digit code to
          <br />
          <b style={{ color: 'var(--text)' }}>{email}</b>
        </p>

        {state.demoOtp && (
          <div className="row gap8 center" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: 999, padding: '7px 12px', margin: '16px auto 0', width: 'fit-content', fontSize: 13, fontWeight: 700 }}>
            ℹ️ Demo mode — use OTP {state.demoOtp}
          </div>
        )}

        <label
          htmlFor="otp"
          className="row gap8 center"
          style={{ marginTop: 24, cursor: 'text' }}
          onClick={() => inputRef.current?.focus()}
        >
          {Array.from({ length: LEN }).map((_, i) => {
            const active = i === digits.length;
            return (
              <span
                key={i}
                style={{
                  width: 44,
                  height: 54,
                  borderRadius: 12,
                  border: `1.5px solid ${error ? 'var(--danger)' : active || i < digits.length ? 'var(--primary)' : 'var(--border)'}`,
                  background: active || i < digits.length ? '#fff' : 'var(--surface-alt)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 800,
                }}
              >
                {digits[i] ?? ''}
              </span>
            );
          })}
        </label>
        <input
          id="otp"
          ref={inputRef}
          value={digits}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          maxLength={LEN}
          style={{ position: 'absolute', opacity: 0, height: 1, width: 1 }}
        />

        {error && <div className="error-text">{error}</div>}

        <button className="btn btn-primary btn-block" style={{ marginTop: 22 }} disabled={digits.length !== LEN || verifying} onClick={() => verify(digits)}>
          {verifying ? 'Verifying…' : 'Verify & Continue'}
        </button>

        <div style={{ marginTop: 16, fontSize: 14 }}>
          {seconds > 0 ? (
            <span className="faint">Resend code in 0:{String(seconds).padStart(2, '0')}</span>
          ) : (
            <button className="link" style={{ background: 'none', border: 'none' }} onClick={() => setSeconds(30)}>
              Resend OTP
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
